import React, { useEffect, useState } from 'react';
import { companyUserAPI, extractApiError } from '../../services/api';
import AlertBanner from '../common/AlertBanner';
import '../../styles/admin/Modal.css';

const CompanyUserModal = ({ user, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    display_name: '',
    email: '',
    password: '',
    role: 'general',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);

  const isEditMode = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        display_name: user.display_name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'general',
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.display_name.trim()) newErrors.display_name = '名前を入力してください';
    if (!formData.email.trim()) newErrors.email = 'メールアドレスを入力してください';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = '有効なメールアドレスを入力してください';

    if (!isEditMode && !formData.password) newErrors.password = 'パスワードを入力してください';
    if (formData.password && formData.password.length < 6) newErrors.password = 'パスワードは6文字以上で入力してください';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const dataToSend = { ...formData };
      if (isEditMode && !dataToSend.password) delete dataToSend.password;
      if (dataToSend.name) 

      if (isEditMode) {
        setLastPayload({ type: 'update', id: user.user_id, data: dataToSend });
        await companyUserAPI.updateUser(user.user_id, dataToSend);
        alert('ユーザー情報を更新しました');
      } else {
        setLastPayload({ type: 'create', data: dataToSend });
        await companyUserAPI.createUser(dataToSend);
        alert('ユーザーを作成しました');
      }
      onSaveSuccess();
    } catch (err) {
      console.error('Failed to save user:', err);
      setServerError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async () => {
    if (!lastPayload) return;
    setServerError(null);
    try {
      if (lastPayload.type === 'update') {
        await companyUserAPI.updateUser(lastPayload.id, lastPayload.data);
      } else {
        await companyUserAPI.createUser(lastPayload.data);
      }
      onSaveSuccess();
    } catch (err) {
      setServerError(extractApiError(err));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'ユーザー情報の編集' : '新規ユーザーの作成'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <AlertBanner message={serverError} onRetry={handleRetry} onClose={() => setServerError(null)} />
          <div className="form-group">
            <label>名前 *</label>
            <input type="text" value={formData.display_name} onChange={(e) => handleChange('display_name', e.target.value)} placeholder="例: 山田太郎" className={errors.display_name ? 'error' : ''} />
            {errors.display_name && <span className="error-message">{errors.display_name}</span>}
          </div>

          <div className="form-group">
            <label>メールアドレス *</label>
            <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="example@company.com" className={errors.email ? 'error' : ''} />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>パスワード {isEditMode ? '(変更する場合のみ入力)' : '*'}</label>
            <input type="password" value={formData.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="6文字以上" className={errors.password ? 'error' : ''} />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label>権限 *</label>
            <select value={formData.role} onChange={(e) => handleChange('role', e.target.value)}>
              <option value="general">一般ユーザー</option>
              <option value="company_admin">企業管理者</option>
            </select>
            <small className="form-help">
              一般ユーザー: 自分の予約のみ管理可能<br />
              企業管理者: 自社の全予約を管理可能
            </small>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={saving}>キャンセル</button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>{saving ? '保存中...' : '保存'}</button>
        </div>
      </div>
    </div>
  );
};

export default CompanyUserModal;
